import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { validateLogin, validateSession, serverLogout, getSessionToken, clearSessionToken, type UserRecord, type UserRole, FULL_ACCESS_ROLES, RESTRICTED_ROLES, userHasRole, userHasAnyRole, userHasFullAccess as _userHasFullAccess, googleLogin as apiGoogleLogin, resetPasswordWithCode as apiResetPassword } from "../../utils/userApi";
import { recordLogin } from "../../utils/profileApi";
import { registerGlobalLogout } from "../../utils/authBridge";
import { toast } from "sonner";

/**
 * AuthContext — global authentication state.
 *
 * Stores the currently logged-in user in sessionStorage so it persists
 * across page refreshes within the same browser tab.
 *
 * SECURITY:
 *   - Login is validated server-side (passwords never sent to client)
 *   - Session tokens are used for authenticated API calls
 *   - Sessions are validated on page refresh
 *   - If server says session is invalid, user is fully logged out
 *     (no stale sessionStorage fallback)
 *   - apiClient triggers global logout on any 401 via authBridge
 *
 * Also manages a "returnUrl" — a URL to redirect to after successful login.
 *
 * Permission matrix (6 roles):
 *   admin         — full access to everything
 *   characterizer — all except delete masters + no user management
 *   designer      — only assigned templates, no delete, no user management
 *   content_writer— only assigned templates, no delete, no user management
 *   developer     — only assigned templates, no delete, no user management
 *   marketer      — only assigned templates, no delete, no user management
 */

const AUTH_STORAGE_KEY = "tpl_auth_user";
const RETURN_URL_KEY = "tpl_auth_return_url";

export interface AuthUser {
  username: string;
  displayName: string;
  role: UserRole;
  additionalRoles?: UserRole[];
  templateAccess: "all" | string[];
}

interface AuthContextType {
  /** Current logged-in user, or null */
  user: AuthUser | null;
  /** Whether auth state has been loaded from storage */
  isReady: boolean;
  /** Login with username + password. Returns true on success. */
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  /** Logout and clear session */
  logout: () => void;
  /** Check if current user is admin */
  isAdmin: boolean;
  /** Check if current user can edit content (all roles can) */
  canEdit: boolean;
  /** Check if current user can manage users (admin only) */
  canManageUsers: boolean;
  /** Check if current user can create/duplicate templates (admin + characterizer) */
  canCreateTemplates: boolean;
  /** Check if current user can delete regular templates (admin + characterizer) */
  canDeleteTemplates: boolean;
  /** Check if current user can delete master templates (admin only) */
  canDeleteMasters: boolean;
  /** Check if current user can edit template metadata (admin + characterizer) */
  canEditTemplateMeta: boolean;
  /** Check if the current user has full template visibility */
  hasFullAccess: boolean;
  /** Check if current user can access a specific template */
  canAccessTemplate: (templateId: string) => boolean;
  /** Set the URL to return to after login */
  setReturnUrl: (url: string) => void;
  /** Get and clear the stored return URL */
  consumeReturnUrl: () => string | null;
  /** Update the in-memory + session user (e.g. after displayName change) */
  updateAuthUser: (updates: Partial<Pick<AuthUser, "displayName">>) => void;
  /** Login via Google ID token */
  loginWithGoogleToken: (idToken: string) => Promise<{ success: boolean; error?: string; errorCode?: string; email?: string }>;
  /** Login after password reset (with verified code + new password) */
  loginWithResetCode: (username: string, code: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

/** Safe no-op default — prevents crash if component renders before provider mounts */
const DEFAULT_AUTH: AuthContextType = {
  user: null,
  isReady: false,
  login: async () => ({ success: false, error: "AuthProvider not mounted" }),
  logout: () => {},
  isAdmin: false,
  canEdit: false,
  canManageUsers: false,
  canCreateTemplates: false,
  canDeleteTemplates: false,
  canDeleteMasters: false,
  canEditTemplateMeta: false,
  hasFullAccess: false,
  canAccessTemplate: () => false,
  setReturnUrl: () => {},
  consumeReturnUrl: () => null,
  updateAuthUser: () => {},
  loginWithGoogleToken: async () => ({ success: false, error: "AuthProvider not mounted" }),
  loginWithResetCode: async () => ({ success: false, error: "AuthProvider not mounted" }),
};

const AuthContext = createContext<AuthContextType>(DEFAULT_AUTH);

function userToAuth(u: UserRecord): AuthUser {
  return {
    username: u.username,
    displayName: u.displayName,
    role: u.role,
    additionalRoles: u.additionalRoles,
    templateAccess: u.templateAccess,
  };
}

/** Helper: clear ALL local auth artifacts */
function purgeLocalAuth() {
  try {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    sessionStorage.removeItem("tpl_mgr_auth");
    sessionStorage.removeItem("__edit_auth__");
  } catch { /* ignore */ }
  clearSessionToken();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // ─── Optimistic init: if we have a cached user in sessionStorage,
  // show the dashboard immediately while validating in the background ───
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const token = getSessionToken();
      if (!token) return null;
      const cached = sessionStorage.getItem(AUTH_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as AuthUser;
        if (parsed && parsed.username && parsed.role) {
          console.log("[Auth] Optimistic init — showing cached user:", parsed.username);
          return parsed;
        }
      }
    } catch { /* ignore */ }
    return null;
  });
  // If we did an optimistic init, mark isReady=true immediately
  const [isReady, setIsReady] = useState(() => {
    try {
      const token = getSessionToken();
      if (!token) return false;
      const cached = sessionStorage.getItem(AUTH_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return !!(parsed && parsed.username && parsed.role);
      }
    } catch { /* ignore */ }
    return false;
  });

  // ─── Background session validation ───
  // If we did an optimistic init, this validates the session silently.
  // If the server says "invalid" -> log out. If network error -> keep cached user.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const token = getSessionToken();

      if (token) {
        // Wait for the warm-up prefetch to finish (wakes edge function from cold start)
        // before attempting session validation
        try {
          const warmup = (window as any).__ZAP_WARMUP;
          if (warmup) await warmup.catch(() => {});
        } catch { /* ignore */ }

        try {
          const freshUser = await validateSession();
          if (!cancelled && freshUser) {
            const authUser = userToAuth(freshUser);
            setUser(authUser);
            try {
              sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authUser));
            } catch { /* ignore */ }
            if (!cancelled) setIsReady(true);
            return;
          }
          // validateSession returned null -> server said session is invalid
          if (!cancelled) {
            console.warn("[Auth] Server rejected session — logging out");
            purgeLocalAuth();
            setUser(null);
            setIsReady(true);
          }
        } catch (err: unknown) {
          // NETWORK_ERROR -> server unreachable (cold start / offline)
          // Keep the optimistic cached user, don't log out
          if ((err as { message?: string })?.message === "NETWORK_ERROR") {
            console.warn("[Auth] Network error during validation — keeping cached user");
            if (!cancelled) setIsReady(true);
            return;
          }
          // Unknown error -> still keep cached user if we have one
          console.warn("[Auth] Unexpected validation error — keeping cached user if available:", err);
          if (!cancelled) setIsReady(true);
        }
        return;
      }

      // No token at all — user is not logged in, nothing to restore.
      if (!cancelled) {
        purgeLocalAuth();
        setUser(null);
        setIsReady(true);
      }
    }

    restoreSession();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const result = await validateLogin(username, password);
      if (!result) {
        return { success: false, error: "\u05E9\u05DD \u05DE\u05E9\u05EA\u05DE\u05E9 \u05D0\u05D5 \u05E1\u05D9\u05E1\u05DE\u05D4 \u05E9\u05D2\u05D5\u05D9\u05D9\u05DD" };
      }
      const authUser = userToAuth(result.user);
      setUser(authUser);
      try {
        sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authUser));
      } catch {
        // ignore storage errors
      }
      // Record login timestamp in profile (non-blocking)
      recordLogin(result.user.username).catch(() => {});
      return { success: true };
    } catch (err: any) {
      console.error("[Auth] Login error:", err);
      const isNetwork = err?.message === "NETWORK_ERROR" || err?.message?.includes("fetch");
      const msg = isNetwork
        ? "\u05E9\u05D2\u05D9\u05D0\u05EA \u05EA\u05E7\u05E9\u05D5\u05E8\u05EA \u05DC\u05E9\u05E8\u05EA \u2014 \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"
        : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA";
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    // Invalidate server session (non-blocking)
    serverLogout().catch(() => {});
    purgeLocalAuth();
  }, []);

  // Register as global logout handler so apiClient can trigger it on 401
  useEffect(() => {
    registerGlobalLogout(() => {
      // Use functional update to check current user without stale closure
      setUser((prev) => {
        if (prev) {
          console.warn("[Auth] Forced logout triggered by 401 response");
          purgeLocalAuth();
          toast.error("\u05D4\u05E1\u05E9\u05DF \u05E4\u05D2 \u2014 \u05D9\u05E9 \u05DC\u05D4\u05EA\u05D7\u05D1\u05E8 \u05DE\u05D7\u05D3\u05E9");
        }
        return null;
      });
    });
    return () => { registerGlobalLogout(null); };
  }, []); // no deps needed — uses functional setUser

  const setReturnUrl = useCallback((url: string) => {
    try {
      sessionStorage.setItem(RETURN_URL_KEY, url);
    } catch {
      // ignore
    }
  }, []);

  const consumeReturnUrl = useCallback((): string | null => {
    try {
      const url = sessionStorage.getItem(RETURN_URL_KEY);
      if (url) {
        sessionStorage.removeItem(RETURN_URL_KEY);
      }
      return url;
    } catch {
      return null;
    }
  }, []);

  /** Patch the current auth user in-memory & sessionStorage */
  const updateAuthUser = useCallback((updates: Partial<Pick<AuthUser, "displayName">>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      try {
        sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // --- Permission flags (multi-role aware) ---
  const role = user?.role;
  const _hr = (r: UserRole) => user ? userHasRole(user, r) : false;
  const _har = (rs: UserRole[]) => user ? userHasAnyRole(user, rs) : false;
  const isAdmin = _hr("admin");
  const canEdit = !!user && !_har(["client_manager", "onboarding"]); // client_manager & onboarding are view-only in templates
  const canManageUsers = isAdmin;
  const canCreateTemplates = _har(["admin", "characterizer"]);
  const canDeleteTemplates = _har(["admin", "characterizer"]);
  const canDeleteMasters = isAdmin;
  const canEditTemplateMeta = _har(["admin", "characterizer"]);
  const hasFullAccess = !!user && _userHasFullAccess(user);

  const canAccessTemplate = useCallback(
    (templateId: string) => {
      if (!user) return false;
      if (_userHasFullAccess(user)) return true;
      if (user.templateAccess === "all") return true;
      return user.templateAccess.includes(templateId);
    },
    [user],
  );

  /* ================================================================
     Session Timeout — auto-logout after 30 min inactivity
     Shows a warning toast 2 minutes before timeout.
     ================================================================ */
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const WARNING_BEFORE_MS = 2 * 60 * 1000;   // warn 2 min before
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShownRef = useRef(false);

  const resetIdleTimers = useCallback(() => {
    if (!user) return;
    warningShownRef.current = false;

    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // Warning toast before logout
    warnTimerRef.current = setTimeout(() => {
      if (!warningShownRef.current) {
        warningShownRef.current = true;
        toast.warning("\u05D4\u05E1\u05E9\u05DF \u05E9\u05DC\u05DA \u05E2\u05D5\u05DE\u05D3 \u05DC\u05E4\u05D5\u05D2 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D4\u05DE\u05E9\u05DA", {
          duration: WARNING_BEFORE_MS,
          id: "session-warning",
        });
      }
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Auto-logout
    idleTimerRef.current = setTimeout(() => {
      toast.dismiss("session-warning");
      logout();
      toast.info("\u05D4\u05EA\u05E0\u05EA\u05E7\u05EA \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA \u05E2\u05E7\u05D1 \u05D7\u05D5\u05E1\u05E8 \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA");
    }, SESSION_TIMEOUT_MS);
  }, [user, logout]);

  /* ================================================================
     Server-side session keepalive — ping /auth/validate every 15 min
     while the user is active. This triggers the sliding-window TTL
     extension on the server so the session never expires mid-work.
     ================================================================ */
  const KEEPALIVE_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
  const lastKeepaliveRef = useRef(Date.now());
  const keepaliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const doKeepalive = useCallback(() => {
    if (!user) return;
    const now = Date.now();
    if (now - lastKeepaliveRef.current < KEEPALIVE_INTERVAL_MS - 30_000) return;
    lastKeepaliveRef.current = now;
    // Fire-and-forget: validate session (server extends TTL)
    validateSession().catch(() => {});
  }, [user]);

  // Register / unregister activity listeners
  useEffect(() => {
    if (!user) return;

    const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"] as const;

    const onActivity = () => {
      if (warningShownRef.current) {
        toast.dismiss("session-warning");
      }
      resetIdleTimers();
    };

    // Start timers initially
    resetIdleTimers();

    // Periodic keepalive: every 15 min, ping the server to extend session
    lastKeepaliveRef.current = Date.now();
    keepaliveTimerRef.current = setInterval(doKeepalive, KEEPALIVE_INTERVAL_MS);

    ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, onActivity, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => document.removeEventListener(ev, onActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (keepaliveTimerRef.current) clearInterval(keepaliveTimerRef.current);
    };
  }, [user, resetIdleTimers, doKeepalive]);

  const loginWithGoogleToken = useCallback(async (idToken: string) => {
    try {
      const result = await apiGoogleLogin(idToken);
      if (!result.success || !result.user) {
        return { success: false, error: result.error, errorCode: result.errorCode, email: result.email };
      }
      const authUser = userToAuth(result.user);
      setUser(authUser);
      try {
        sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authUser));
      } catch {
        // ignore storage errors
      }
      recordLogin(result.user.username).catch(() => {});
      return { success: true };
    } catch (err: any) {
      console.error("[Auth] Google login error:", err);
      const isNetwork = err?.message === "NETWORK_ERROR" || err?.message?.includes("fetch");
      const msg = isNetwork
        ? "\u05E9\u05D2\u05D9\u05D0\u05EA \u05EA\u05E7\u05E9\u05D5\u05E8\u05EA \u05DC\u05E9\u05E8\u05EA \u2014 \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"
        : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA";
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, []);

  const loginWithResetCode = useCallback(async (username: string, code: string, newPassword: string) => {
    try {
      const result = await apiResetPassword(username, code, newPassword);
      if (!result.success || !result.user) {
        return { success: false, error: result.error };
      }
      const authUser = userToAuth(result.user);
      setUser(authUser);
      try {
        sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authUser));
      } catch {
        // ignore storage errors
      }
      recordLogin(result.user.username).catch(() => {});
      return { success: true };
    } catch (err: any) {
      console.error("[Auth] Reset password login error:", err);
      const isNetwork = err?.message === "NETWORK_ERROR" || err?.message?.includes("fetch");
      const msg = isNetwork
        ? "\u05E9\u05D2\u05D9\u05D0\u05EA \u05EA\u05E7\u05E9\u05D5\u05E8\u05EA \u05DC\u05E9\u05E8\u05EA \u2014 \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"
        : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA";
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isReady,
        login,
        logout,
        isAdmin,
        canEdit,
        canManageUsers,
        canCreateTemplates,
        canDeleteTemplates,
        canDeleteMasters,
        canEditTemplateMeta,
        hasFullAccess,
        canAccessTemplate,
        setReturnUrl,
        consumeReturnUrl,
        updateAuthUser,
        loginWithGoogleToken,
        loginWithResetCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** Get auth context — always returns a valid value (safe default if outside provider) */
export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

/** Safe version — returns null outside AuthProvider (kept for backward compat) */
export function useAuthSafe(): AuthContextType | null {
  return useContext(AuthContext);
}